import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { limitToLast, onValue, orderByKey, query, ref, set, update } from "firebase/database";
import { rtdb } from "../firebase";
import { useAuth } from "./AuthContext";
import { useMockLocation } from "./MockLocationContext";
import { useCurrentTrips } from "../hooks/useCurrentTrips";
import { effectiveTripSharing } from "../utils/locationSharing";
import { createLocationSampler } from "../utils/locationSampler";

export interface LocationPoint {
  lat: number;
  lng: number;
  /** epoch milliseconds */
  t: number;
  src: "phone" | "watch" | "web";
}

export interface TrackingConfig {
  trackingEnabled: boolean;
  trackPhone: boolean;
  trackWatch: boolean;
}

const DEFAULT_CONFIG: TrackingConfig = {
  trackingEnabled: false,
  trackPhone: true,
  trackWatch: false,
};

/** Normalise a raw config (RTDB or storage) onto the defaults. */
function toTrackingConfig(val: Partial<TrackingConfig> | null | undefined): TrackingConfig {
  return {
    trackingEnabled: val?.trackingEnabled === true,
    trackPhone: val?.trackPhone !== false,
    trackWatch: val?.trackWatch === true,
  };
}

// The web RTDB client has no disk persistence, so an app opened offline never
// hears the config at all — and tracking would sit at the default (off) for an
// entire cruise. The last config we saw is kept per account in localStorage
// and used until RTDB answers.
const CONFIG_CACHE_KEY_PREFIX = "triptracker.trackingConfig.";

function readCachedConfig(uid: string): TrackingConfig | null {
  try {
    const raw = localStorage.getItem(CONFIG_CACHE_KEY_PREFIX + uid);
    return raw ? toTrackingConfig(JSON.parse(raw) as Partial<TrackingConfig>) : null;
  } catch {
    return null;
  }
}

function writeCachedConfig(uid: string, config: TrackingConfig): void {
  try {
    localStorage.setItem(CONFIG_CACHE_KEY_PREFIX + uid, JSON.stringify(config));
  } catch {
    // Storage unavailable (private mode, quota) — we just lose the offline seed.
  }
}

interface LocationTrackingContextValue {
  /** RTDB config for this device/account. */
  config: TrackingConfig;
  configLoaded: boolean;
  /** True while the geolocation watch is actively running. */
  capturing: boolean;
  /** Last GPS point written to RTDB this session, if any. */
  lastPoint: LocationPoint | null;
  /** Most recent point for this user from RTDB, across all devices/sources. */
  latestPoint: LocationPoint | null;
  /** A human-readable geolocation error, if the last attempt failed. */
  error: string | null;
  /**
   * Why the mock override failed to reach the shared config, if it did.
   *
   * Surfaced rather than logged because the failure is invisible from the UI:
   * the card still shows the mock set (it is, locally), while the watch never
   * hears about it. A console.error only reaches people holding the
   * `view_error_details` role.
   */
  mockSyncError: string | null;
  /** Whether some current trip would have this user's points captured. */
  hasSharingCurrentTrip: boolean;
  /** Master switch — enable/disable all location capture (writes RTDB config). */
  setTrackingEnabled: (enabled: boolean) => Promise<void>;
  /** Per-device: capture from the phone (this browser + the native phone app). */
  setTrackPhone: (enabled: boolean) => Promise<void>;
  /** Per-device: capture from the paired watch. */
  setTrackWatch: (enabled: boolean) => Promise<void>;
}

const LocationTrackingContext = createContext<LocationTrackingContextValue | null>(null);

export function LocationTrackingProvider({ children }: { children: ReactNode }) {
  const { user, userProfile } = useAuth();
  const { mock } = useMockLocation();
  const { currentTrips } = useCurrentTrips();

  const [config, setConfig] = useState<TrackingConfig>(DEFAULT_CONFIG);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [lastPoint, setLastPoint] = useState<LocationPoint | null>(null);
  const [latestPoint, setLatestPoint] = useState<LocationPoint | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mockSyncError, setMockSyncError] = useState<string | null>(null);

  // Subscribe to the user's tracking config in RTDB.
  useEffect(() => {
    if (!user) {
      setConfig(DEFAULT_CONFIG);
      setConfigLoaded(false);
      return;
    }
    const uid = user.uid;
    // Seed from the last-known config until RTDB answers (see readCachedConfig).
    // `configLoaded` stays false: it means "RTDB has spoken", and the mock sync
    // below relies on that to avoid writing back a stale value.
    setConfig(readCachedConfig(uid) ?? DEFAULT_CONFIG);
    const configRef = ref(rtdb, `userLocations/${uid}/config`);
    const unsub = onValue(
      configRef,
      (snap) => {
        const next = toTrackingConfig(snap.val() as Partial<TrackingConfig> | null);
        setConfig(next);
        writeCachedConfig(uid, next);
        setConfigLoaded(true);
      },
      () => {
        setConfig(DEFAULT_CONFIG);
        setConfigLoaded(true);
      },
    );
    return unsub;
  }, [user]);

  // Subscribe to the newest point across ALL sources (watch, native phone,
  // web). This browser only writes its own points, so this is what tells the
  // UI a paired device is actively sharing even when this tab isn't capturing.
  useEffect(() => {
    if (!user) {
      setLatestPoint(null);
      return;
    }
    const pointsQuery = query(
      ref(rtdb, `userLocations/${user.uid}/points`),
      orderByKey(),
      limitToLast(1),
    );
    const unsub = onValue(
      pointsQuery,
      (snap) => {
        let newest: LocationPoint | null = null;
        snap.forEach((child) => {
          const v = child.val() as Partial<LocationPoint> | null;
          if (
            v &&
            typeof v.lat === "number" &&
            typeof v.lng === "number" &&
            typeof v.t === "number"
          ) {
            newest = {
              lat: v.lat,
              lng: v.lng,
              t: v.t,
              src: v.src === "phone" || v.src === "watch" ? v.src : "web",
            };
          }
        });
        setLatestPoint(newest);
      },
      () => setLatestPoint(null),
    );
    return unsub;
  }, [user]);

  const defaultSharing = userProfile?.defaultLocationSharing === true;

  const hasSharingCurrentTrip = useMemo(() => {
    if (!user) return false;
    return currentTrips.some((trip) => effectiveTripSharing(trip, user.uid, defaultSharing));
  }, [currentTrips, user, defaultSharing]);

  const shouldCapture =
    !!user && config.trackingEnabled && config.trackPhone && hasSharingCurrentTrip;

  // Decides which fixes get saved (accuracy, staleness, moved far enough —
  // see locationSampler). A ref so the geolocation callback, created once per
  // effect run, keeps its memory of the last saved fix.
  const samplerRef = useRef(createLocationSampler());

  useEffect(() => {
    if (!user || !shouldCapture) {
      setCapturing(false);
      return;
    }
    const uid = user.uid;

    // Test override: emit the mock point on a timer instead of reading the
    // device GPS. Lets the Journey trail be exercised from the Mock Location
    // card without physically moving.
    if (mock) {
      setError(null);
      setCapturing(true);
      const writeMock = () => {
        const now = Date.now();
        const point: LocationPoint = { lat: mock.lat, lng: mock.lng, t: now, src: "web" };
        set(ref(rtdb, `userLocations/${uid}/points/${now}`), point).catch((err) => {
          console.error("Failed to write location point:", err);
        });
        setLastPoint(point);
      };
      writeMock();
      const intervalId = setInterval(writeMock, 60_000);
      return () => {
        clearInterval(intervalId);
        setCapturing(false);
      };
    }

    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setError("Location tracking isn't supported on this device.");
      setCapturing(false);
      return;
    }

    setError(null);
    setCapturing(true);
    samplerRef.current.reset();

    const onPosition = (pos: GeolocationPosition) => {
      const now = Date.now();
      // The fix's own time, not now: a cached fix stamped "now" would place us
      // where we were a while ago. Clamped in case the clocks disagree.
      const t = Math.min(Math.round(pos.timestamp), now);
      const fix = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracyM: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
        t,
      };
      if (!samplerRef.current.accept(fix, now)) return;
      const point: LocationPoint = { lat: fix.lat, lng: fix.lng, t, src: "web" };
      set(ref(rtdb, `userLocations/${uid}/points/${t}`), point).catch((err) => {
        console.error("Failed to write location point:", err);
      });
      setLastPoint(point);
    };

    const onError = (err: GeolocationPositionError) => {
      setError(
        err.code === err.PERMISSION_DENIED
          ? "Location permission denied — re-enable it in your browser to share your location."
          : err.message || "Couldn't read your location.",
      );
      // A permission denial won't recover without user action; stop the watch.
      if (err.code === err.PERMISSION_DENIED) {
        navigator.geolocation.clearWatch(watchId);
        setCapturing(false);
      }
    };

    const watchId = navigator.geolocation.watchPosition(onPosition, onError, {
      enableHighAccuracy: true,
      // Fresh fixes only — the sampler decides what's worth saving.
      maximumAge: 5_000,
      timeout: 27_000,
    });

    return () => {
      navigator.geolocation.clearWatch(watchId);
      setCapturing(false);
    };
  }, [user, shouldCapture, mock]);

  const writeConfig = useCallback(
    (next: TrackingConfig): Promise<void> => {
      if (!user) return Promise.resolve();
      // Applied locally first. An RTDB listener only fires once it holds the
      // whole node, which it never does if the app started offline — so
      // without this the toggle wouldn't move until we reconnected. Cached too,
      // so the choice survives a restart mid-cruise.
      setConfig(next);
      writeCachedConfig(user.uid, next);
      // `update`, not `set`: the mock-location override shares this node, and
      // a whole-node write would drop it every time a toggle is flipped.
      //
      // Not awaited: the promise only settles on the server's ack, which
      // offline means "not until we're back on land". RTDB queues the write and
      // sends it on reconnect.
      update(ref(rtdb, `userLocations/${user.uid}/config`), next).catch((err: unknown) => {
        console.error("Failed to save tracking config:", err);
      });
      return Promise.resolve();
    },
    [user],
  );

  // Mirror the device-location override into the same config node the watch
  // already listens to, so one mock set from the Admin card reaches every
  // client. The watch has no mock UI of its own, and a Wear emulator can't
  // sign in (it waits on a token from the paired phone), so without this
  // there's no way to exercise its nearby list off-site.
  //
  // Cleared by writing nulls, which removes the keys rather than leaving a
  // stale point the watch would keep trusting.
  useEffect(() => {
    // Wait for the config listener: the write below has to carry
    // `trackingEnabled`, and sending a stale default would flip the user's
    // real setting.
    if (!user || !configLoaded) return;
    update(ref(rtdb, `userLocations/${user.uid}/config`), {
      // Resent unchanged, not because it's ours to set, but because the node's
      // validate rule is `hasChildren(['trackingEnabled'])` — it validates the
      // merged result, so a mock-only write to a config that doesn't exist yet
      // is rejected outright. Anyone who has never opened the tracking
      // settings has no config node, which is exactly the person most likely
      // to be reaching for a test override.
      trackingEnabled: config.trackingEnabled,
      mockLat: mock ? mock.lat : null,
      mockLng: mock ? mock.lng : null,
      mockLabel: mock?.label ?? null,
    })
      .then(() => setMockSyncError(null))
      .catch((err: unknown) => {
        console.error("Failed to sync mock location to config:", err);
        setMockSyncError(
          err instanceof Error ? err.message : "Couldn't reach the shared config.",
        );
      });
  }, [user, mock, configLoaded, config.trackingEnabled]);

  const setTrackingEnabled = useCallback(
    (enabled: boolean) =>
      writeConfig({
        trackingEnabled: enabled,
        trackPhone: config.trackPhone,
        trackWatch: config.trackWatch,
      }),
    [writeConfig, config.trackPhone, config.trackWatch],
  );

  const setTrackPhone = useCallback(
    (enabled: boolean) =>
      writeConfig({
        trackingEnabled: config.trackingEnabled,
        trackPhone: enabled,
        trackWatch: config.trackWatch,
      }),
    [writeConfig, config.trackingEnabled, config.trackWatch],
  );

  const setTrackWatch = useCallback(
    (enabled: boolean) =>
      writeConfig({
        trackingEnabled: config.trackingEnabled,
        trackPhone: config.trackPhone,
        trackWatch: enabled,
      }),
    [writeConfig, config.trackingEnabled, config.trackPhone],
  );

  const value: LocationTrackingContextValue = {
    config,
    configLoaded,
    capturing,
    lastPoint,
    latestPoint,
    error,
    hasSharingCurrentTrip,
    mockSyncError,
    setTrackingEnabled,
    setTrackPhone,
    setTrackWatch,
  };

  return (
    <LocationTrackingContext.Provider value={value}>{children}</LocationTrackingContext.Provider>
  );
}

export function useLocationTracking(): LocationTrackingContextValue {
  const ctx = useContext(LocationTrackingContext);
  if (!ctx) {
    throw new Error("useLocationTracking must be used within a LocationTrackingProvider");
  }
  return ctx;
}
